/**
 * Dashboard-specific date utilities
 * Provides consistent date handling across dashboard components
 */

/**
 * Format a relative time string (e.g., "2 hours ago", "yesterday")
 */
export const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const target = new Date(date);
  const diffMs = now.getTime() - target.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  
  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffWeek === 1) return '1 week ago';
  if (diffWeek < 4) return `${diffWeek}w ago`;
  if (diffMonth === 1) return '1 month ago';
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  
  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear}y ago`;
};

/**
 * Format time until due date (e.g., "Due in 2 hours", "Overdue by 3 days")
 */
export const formatTimeUntilDue = (dueDate: Date | string): string => {
  const now = new Date();
  const due = new Date(dueDate);
  const diffMs = due.getTime() - now.getTime();
  const isOverdue = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);
  
  const diffSec = Math.floor(absDiffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  const prefix = isOverdue ? 'Overdue by' : 'Due in';
  
  if (diffSec < 60) return isOverdue ? 'Just overdue' : 'Due now';
  if (diffMin < 60) return `${prefix} ${diffMin}m`;
  if (diffHour < 24) return `${prefix} ${diffHour}h`;
  if (diffDay === 1) return isOverdue ? 'Overdue by 1 day' : 'Due tomorrow';
  
  return `${prefix} ${diffDay}d`;
};

/**
 * Get a friendly date string for dashboard display
 */
export const formatFriendlyDate = (date: Date | string): string => {
  const target = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  const targetDate = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  
  if (targetDate.getTime() === today.getTime()) return 'Today';
  if (targetDate.getTime() === yesterday.getTime()) return 'Yesterday';
  if (targetDate.getTime() === tomorrow.getTime()) return 'Tomorrow';
  
  // This week
  const daysDiff = Math.floor((targetDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (Math.abs(daysDiff) < 7) {
    const dayName = target.toLocaleDateString('en-US', { weekday: 'long' });
    return daysDiff > 0 ? `This ${dayName}` : `Last ${dayName}`;
  }
  
  // Use short format for other dates
  return target.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: target.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
  });
};

/**
 * Get time of day label from hour
 */
export const getTimeOfDayLabel = (hour: number): string => {
  if (hour >= 5 && hour < 12) return 'Morning';
  if (hour >= 12 && hour < 17) return 'Afternoon';
  if (hour >= 17 && hour < 21) return 'Evening';
  return 'Night';
};

/**
 * Format duration in hours to human readable format
 */
export const formatDuration = (hours: number): string => {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes}m`;
  }
  
  if (hours < 24) {
    return `${Math.round(hours * 10) / 10}h`;
  }
  
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round((hours % 24) * 10) / 10;
  
  if (remainingHours === 0) return `${days}d`;
  return `${days}d ${remainingHours}h`;
};

/**
 * Get week start date (Monday)
 */
export const getWeekStart = (date: Date = new Date()): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  return new Date(d.setDate(diff));
};

/**
 * Get week end date (Sunday)
 */
export const getWeekEnd = (date: Date = new Date()): Date => {
  const weekStart = getWeekStart(date);
  return new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
};

/**
 * Get array of dates for the current week
 */
export const getWeekDates = (startDate: Date = new Date()): Date[] => {
  const weekStart = getWeekStart(startDate);
  return Array.from({ length: 7 }, (_, i) => 
    new Date(weekStart.getTime() + i * 24 * 60 * 60 * 1000)
  );
};

/**
 * Get month start date
 */
export const getMonthStart = (date: Date = new Date()): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Get month end date
 */
export const getMonthEnd = (date: Date = new Date()): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: Date | string, date2: Date | string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.toDateString() === d2.toDateString();
};

/**
 * Check if date is in current week
 */
export const isThisWeek = (date: Date | string): boolean => {
  const target = new Date(date);
  const weekStart = getWeekStart();
  const weekEnd = getWeekEnd();
  return target >= weekStart && target <= weekEnd;
};

/**
 * Check if date is in current month
 */
export const isThisMonth = (date: Date | string): boolean => {
  const target = new Date(date);
  const now = new Date();
  return target.getMonth() === now.getMonth() && 
         target.getFullYear() === now.getFullYear();
};

/**
 * Get business days between two dates (excludes weekends)
 */
export const getBusinessDaysBetween = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let businessDays = 0;
  
  const currentDate = new Date(start);
  while (currentDate <= end) {
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday (0) or Saturday (6)
      businessDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return businessDays;
};

/**
 * Format time for display (e.g., "2:30 PM")
 */
export const formatTime = (date: Date | string): string => {
  const target = new Date(date);
  return target.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

/**
 * Format date and time for display
 */
export const formatDateTime = (date: Date | string): string => {
  const target = new Date(date);
  const dateStr = formatFriendlyDate(target);
  const timeStr = formatTime(target);
  return `${dateStr} at ${timeStr}`;
};

/**
 * Get days until date (negative if past)
 */
export const getDaysUntil = (date: Date | string): number => {
  const target = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  
  return Math.floor((targetDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

/**
 * Export all date helpers as a namespace
 */
export const DateHelpers = {
  formatRelativeTime,
  formatTimeUntilDue,
  formatFriendlyDate,
  getTimeOfDayLabel,
  formatDuration,
  getWeekStart,
  getWeekEnd,
  getWeekDates,
  getMonthStart,
  getMonthEnd,
  isSameDay,
  isThisWeek,
  isThisMonth,
  getBusinessDaysBetween,
  formatTime,
  formatDateTime,
  getDaysUntil
};